import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/lib/auth';
import { isCell } from '@/lib/cell/config';
import { cellGet } from '@/lib/cell/api';

/**
 * Does the signed-in pro have a spotlight AT ALL? (Peter, 2026-08-20: "I may not
 * want to ever spotlight some of my pros... would it be possible for Spotlight
 * not to show up at all until I send the first one?")
 *
 * The word "Spotlight" appears NOWHERE in a pro's portal until this is true —
 * not the menu, not the More sheet, not the home card. A visible section is a
 * promise the studio may not intend to keep for that person; the section
 * materializes the day the owner sends one, and being featured becomes an
 * event that arrives rather than a queue they sit in.
 *
 * Filtered by the pro's own id explicitly (never bare RLS — operators can read
 * a whole location's rows, and an operator viewing the hub must not light the
 * section up from someone else's spotlight). 'Deleted' rows don't count.
 */
export function useHasSpotlight(): boolean {
  const { session } = useSession();
  const userId = session?.user?.id;
  const { data } = useQuery<boolean>({
    queryKey: ['has-spotlight', userId],
    enabled: !!userId,
    staleTime: 60_000,
    queryFn: async () => {
      if (isCell()) {
        // CELL (M28): GET /v1/pro-hub/users/{authUserId}/spotlight — the pro, then a non-Deleted spotlight by the pro's
        // own id (never bare row security), in one call; errors read as "none", as below.
        const { data } = await cellGet<{ pro: { id: string } | null; spotlight: { id: string } | null }>(
          `/v1/pro-hub/users/${encodeURIComponent(userId!)}/spotlight`);
        return !!data?.pro && !!data?.spotlight;
      }
      const { data: pro } = await supabase
        .from('pros').select('id').eq('auth_user_id', userId!).maybeSingle();
      if (!pro) return false;
      // CELL (M28): production only — the cell path is the isCell() branch above, which returns first.
      const { data: sp } = await supabase
        .from('spotlights').select('id')
        .eq('pro_id', pro.id)
        .neq('status', 'Deleted')
        .limit(1)
        .maybeSingle();
      return !!sp;
    },
  });
  return data ?? false;
}
